import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  ConfirmationModal,
  FilterLabel,
  FilterReset,
  LoadingSpinner,
  Message,
  PagedTable,
  SelectOption,
  SortEvent,
  Th,
  ThSortable,
  formatDatetime,
  useBoundForm
} from 'common.ui';
import { accessRequestClient, usersClient } from 'api/MinSideClients';
import {
  ApprovedAccessOrderBy,
  ApprovedAccessResponse,
  GenericValidationError,
  MinSideAccessControl,
  ResponseError,
  SortOrder,
  UserAccessType
} from 'api/minside';
import Page from 'components/page/Page';
import { ReactComponent as IconDelete } from 'assets/icons/delete-icon.svg';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useApplicableApplications } from 'api/hooks/useApplicableApplications';
import { useRolesQuery } from 'api/hooks/useRolesQuery';
import { usePermissionsQuery } from 'api/hooks/usePermissionsQuery';
import { useAccessControlList } from 'hooks/useAccessControlList';

const PAGE_SIZE = 20;
const DEFAULT_SORT_FIELD = ApprovedAccessOrderBy.AssignedOn;
const DEFAULT_SORT_DIR = 'desc';

type SearchForm = {
  firstName: string;
  surname: string;
  applicationIds: string[];
  roleIds: string[];
  permissionIds: string[];
};

type UrlSearch = SearchForm & {
  approvedPage: string;
  approvedSortField: string;
  approvedSortDir: string;
  assignedPage: string;
  assignedSortField: string;
  assignedSortDir: string;
};

const URL_DEFAULTS: UrlSearch = {
  firstName: '',
  surname: '',
  applicationIds: [],
  roleIds: [],
  permissionIds: [],
  approvedPage: '1',
  approvedSortField: DEFAULT_SORT_FIELD,
  approvedSortDir: DEFAULT_SORT_DIR,
  assignedPage: '1',
  assignedSortField: DEFAULT_SORT_FIELD,
  assignedSortDir: DEFAULT_SORT_DIR
};

function useUrlState<T extends Record<string, string | string[]>>(defaults: T) {
  const location = useLocation();
  const history = useHistory();

  const current = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const out: Record<string, string | string[]> = { ...defaults };
    for (const key of Object.keys(defaults)) {
      if (Array.isArray(defaults[key as keyof T])) {
        // Tomme verdier fra URL-en ("applicationIds=") er ikke et filtervalg, og må ikke
        // bli et filter på tom streng.
        const vals = params.getAll(key).filter((v) => v !== '');
        if (vals.length > 0) out[key] = vals;
      } else {
        const v = params.get(key);
        if (v !== null) out[key] = v;
      }
    }
    return out as T;
  }, [location.search, defaults]);

  const set = useCallback(
    (next: Partial<T>) => {
      const params = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(next)) {
        params.delete(k);
        if (Array.isArray(v)) v.forEach((x) => params.append(k, String(x)));
        else if (v != null && v !== '') params.set(k, String(v));
      }
      history.replace({ ...location, search: params.toString() });
    },
    [location, history]
  );

  return [current, set] as const;
}

// Tomme lister må utelates helt fra forespørselen. Den genererte klienten serialiserer [] til
// "roleIds=", som model binding i API-et avviser (tallister) eller tolker som et tomt filterverdi (strenglister).
function listParam<T>(values: T[]): T[] | undefined {
  return values.length > 0 ? values : undefined;
}

function hasFilters(s: SearchForm): boolean {
  return !!(s.firstName || s.surname || s.applicationIds.length || s.roleIds.length || s.permissionIds.length);
}

function ApprovedAccessRequests() {
  const { t } = useTranslation('pages', { keyPrefix: 'accessRequest.approvedAccessRequests' });

  // Tildelere (Rovbase) har EndUserCreate og ser brukerne de forvalter (assigned-users).
  // ExternalApprovers mangler denne og ser de rettighetene de selv kan fjerne (approved).
  // Vent til ACL faktisk er lastet før vi velger tabell - ellers tolkes "ukjent" som "ikke Tildeler".
  const [acl, hasAccess] = useAccessControlList();
  const aclLoaded = acl !== undefined;
  const canCreateEndUser = hasAccess(MinSideAccessControl.EndUserCreate);

  const [search, setSearch] = useUrlState<UrlSearch>(URL_DEFAULTS);
  const [formValues, setFormValues] = useState<SearchForm>(() => ({
    firstName: search.firstName,
    surname: search.surname,
    applicationIds: search.applicationIds,
    roleIds: search.roleIds,
    permissionIds: search.permissionIds
  }));
  const [formKey, setFormKey] = useState(0);
  const [removeAccess, setRemoveAccess] = useState<ApprovedAccessResponse | undefined>();

  const [applications] = useApplicableApplications();

  const applicableAppIds = (applications as SelectOption[] | undefined)?.map((a) => a.id as string);
  const selectedAppIds = formValues.applicationIds.filter((id) => id !== 'global');
  const roleFilterAppIds = selectedAppIds.length > 0 ? selectedAppIds : applicableAppIds;

  const { data: rolesData } = useRolesQuery(roleFilterAppIds);
  const { data: permissionsData } = usePermissionsQuery(roleFilterAppIds);

  const queryClient = useQueryClient();

  const approvedPage = Number(search.approvedPage) || 1;
  const approvedSorting = {
    field: (search.approvedSortField || DEFAULT_SORT_FIELD) as ApprovedAccessOrderBy,
    direction: (search.approvedSortDir || DEFAULT_SORT_DIR) as 'asc' | 'desc'
  };

  const assignedPage = Number(search.assignedPage) || 1;
  const assignedSorting = {
    field: (search.assignedSortField || DEFAULT_SORT_FIELD) as ApprovedAccessOrderBy,
    direction: (search.assignedSortDir || DEFAULT_SORT_DIR) as 'asc' | 'desc'
  };

  const sharedFilter = {
    firstName: search.firstName || undefined,
    surname: search.surname || undefined,
    roleIds: listParam(search.roleIds.map(Number)),
    applicationIds: listParam(search.applicationIds.filter((a) => a !== 'global')),
    includeGlobalApplications: search.applicationIds.includes('global')
  };

  const filterKey = {
    firstName: search.firstName,
    surname: search.surname,
    applicationIds: search.applicationIds,
    roleIds: search.roleIds,
    permissionIds: search.permissionIds
  };

  const { data: approvedData, isLoading: isLoadingApproved } = useQuery({
    queryKey: ['approvedAccess', filterKey, approvedPage, approvedSorting],
    queryFn: () =>
      accessRequestClient.apiAccessRequestsApprovedGet({
        pageSize: PAGE_SIZE,
        pageNumber: approvedPage,
        sortOrder: approvedSorting.direction === 'asc' ? SortOrder.Ascending : SortOrder.Descending,
        orderField: approvedSorting.field,
        ...sharedFilter,
        permissionIds: listParam(search.permissionIds.map(Number))
      }),
    enabled: aclLoaded && !canCreateEndUser,
    keepPreviousData: true,
    staleTime: 30_000
  });

  const { data: assignedData, isLoading: isLoadingAssigned } = useQuery({
    queryKey: ['assignedUsers', filterKey, assignedPage, assignedSorting],
    queryFn: () =>
      accessRequestClient.apiAccessRequestsAssignedUsersGet({
        pageSize: PAGE_SIZE,
        pageNumber: assignedPage,
        sortOrder: assignedSorting.direction === 'asc' ? SortOrder.Ascending : SortOrder.Descending,
        orderField: assignedSorting.field,
        ...sharedFilter
      }),
    enabled: aclLoaded && canCreateEndUser,
    keepPreviousData: true,
    staleTime: 30_000
  });

  const { form, FormContainer, Input, MultiSelect } = useBoundForm<SearchForm>({
    onSubmit: async (values) => {
      setFormValues(values);
      setSearch({ ...values, approvedPage: '1', assignedPage: '1' });
    },
    model: formValues
  });

  function applyFilterRemoval(update: Partial<SearchForm>) {
    const next = { ...formValues, ...update };
    setFormValues(next);
    setFormKey((k) => k + 1);
    setSearch({ ...next, approvedPage: '1', assignedPage: '1' });
  }

  function resetAll() {
    const empty: SearchForm = { firstName: '', surname: '', applicationIds: [], roleIds: [], permissionIds: [] };
    setFormValues(empty);
    setFormKey((k) => k + 1);
    setSearch({
      ...empty,
      approvedPage: '1',
      approvedSortField: DEFAULT_SORT_FIELD,
      approvedSortDir: DEFAULT_SORT_DIR,
      assignedPage: '1',
      assignedSortField: DEFAULT_SORT_FIELD,
      assignedSortDir: DEFAULT_SORT_DIR
    });
  }

  const handleApprovedSort = (e: SortEvent<ApprovedAccessOrderBy>) => {
    setSearch({ approvedSortField: e.field, approvedSortDir: e.direction, approvedPage: '1' });
  };

  const handleAssignedSort = (e: SortEvent<ApprovedAccessOrderBy>) => {
    setSearch({ assignedSortField: e.field, assignedSortDir: e.direction, assignedPage: '1' });
  };

  // Spinner til ACL er kjent, deretter kun den aktive tabellens query. Vi OR-er ikke begge query-flaggene,
  // for en disabled query fullfører aldri og ville holdt spinneren gående.
  const isLoading = !aclLoaded || (canCreateEndUser ? isLoadingAssigned : isLoadingApproved);

  function renderApprovedTable() {
    if (!approvedData) return null;

    return (
      <PagedTable
        startIndex={(approvedPage - 1) * PAGE_SIZE}
        totalHits={approvedData.pageInfo?.totalCount ?? 0}
        pageSize={PAGE_SIZE}
        onNextClick={() => setSearch({ approvedPage: String(approvedPage + 1) })}
        onPreviousClick={() => setSearch({ approvedPage: String(approvedPage - 1) })}
        nextText={t('table.pagination.nextText')}
        previousText={t('table.pagination.previousText')}
        ofText={t('table.pagination.ofText')}
        noRowsFoundText={t('table.noRowsFoundText')}
        renderRowCount={(rowsNr) => t('table.rowCountText', { rowsNr })}
      >
        <thead>
          <tr>
            <ThSortable
              title={t('table.headers.name')}
              currentSort={approvedSorting}
              onSort={handleApprovedSort}
              field={ApprovedAccessOrderBy.UserDisplayName}
            />
            <ThSortable
              title={t('table.headers.email')}
              currentSort={approvedSorting}
              onSort={handleApprovedSort}
              field={ApprovedAccessOrderBy.UserEmail}
            />
            <ThSortable
              title={t('table.headers.telephone')}
              currentSort={approvedSorting}
              onSort={handleApprovedSort}
              field={ApprovedAccessOrderBy.UserMfaPhone}
            />
            <ThSortable
              title={t('table.headers.accessRightAndOrganization')}
              currentSort={approvedSorting}
              onSort={handleApprovedSort}
              field={ApprovedAccessOrderBy.AccessName}
            />
            <ThSortable
              title={t('table.headers.applicationName')}
              currentSort={approvedSorting}
              onSort={handleApprovedSort}
              field={ApprovedAccessOrderBy.ApplicationName}
            />
            <ThSortable
              title={t('table.headers.date')}
              currentSort={approvedSorting}
              onSort={handleApprovedSort}
              field={ApprovedAccessOrderBy.AssignedOn}
            />
            <Th title={t('table.headers.remove')} />
          </tr>
        </thead>
        <tbody>
          {approvedData.data?.map((u: ApprovedAccessResponse) => (
            <tr key={`${u.accessType}${u.accessId}${u.userId}${u.organizationId}`}>
              <td>{u.userDisplayName}</td>
              <td>{u.userEmail}</td>
              <td>{u.userMfaPhone}</td>
              <td>
                {u.accessName}
                {u.organizationName ? ` / ${u.organizationName}` : ''}
              </td>
              <td>{u.applicationName}</td>
              <td>{u.assignedOn && formatDatetime(u.assignedOn, true)}</td>
              <td>
                <IconDelete onClick={() => setRemoveAccess(u)} />
              </td>
            </tr>
          ))}
        </tbody>
      </PagedTable>
    );
  }

  function renderAssignedTable() {
    if (!assignedData) return null;

    return (
      <PagedTable
        startIndex={(assignedPage - 1) * PAGE_SIZE}
        totalHits={assignedData.pageInfo?.totalCount ?? 0}
        pageSize={PAGE_SIZE}
        onNextClick={() => setSearch({ assignedPage: String(assignedPage + 1) })}
        onPreviousClick={() => setSearch({ assignedPage: String(assignedPage - 1) })}
        nextText={t('table.pagination.nextText')}
        previousText={t('table.pagination.previousText')}
        ofText={t('table.pagination.ofText')}
        noRowsFoundText={t('table.noRowsFoundText')}
        renderRowCount={(rowsNr) => t('table.rowCountText', { rowsNr })}
      >
        <thead>
          <tr>
            <ThSortable
              title={t('table.headers.name')}
              currentSort={assignedSorting}
              onSort={handleAssignedSort}
              field={ApprovedAccessOrderBy.UserDisplayName}
            />
            <ThSortable
              title={t('table.headers.email')}
              currentSort={assignedSorting}
              onSort={handleAssignedSort}
              field={ApprovedAccessOrderBy.UserEmail}
            />
            <ThSortable
              title={t('table.headers.telephone')}
              currentSort={assignedSorting}
              onSort={handleAssignedSort}
              field={ApprovedAccessOrderBy.UserMfaPhone}
            />
            <ThSortable
              title={t('table.headers.accessRightAndOrganization')}
              currentSort={assignedSorting}
              onSort={handleAssignedSort}
              field={ApprovedAccessOrderBy.AccessName}
            />
            <ThSortable
              title={t('table.headers.applicationName')}
              currentSort={assignedSorting}
              onSort={handleAssignedSort}
              field={ApprovedAccessOrderBy.ApplicationName}
            />
            <ThSortable
              title={t('table.headers.date')}
              currentSort={assignedSorting}
              onSort={handleAssignedSort}
              field={ApprovedAccessOrderBy.AssignedOn}
            />
            <Th title={t('table.headers.remove')} />
          </tr>
        </thead>
        <tbody>
          {assignedData.data?.map((u: ApprovedAccessResponse) => (
            <tr key={`${u.accessType}${u.accessId}${u.userId}${u.organizationId}`}>
              <td>{u.userDisplayName}</td>
              <td>{u.userEmail}</td>
              <td>{u.userMfaPhone}</td>
              <td>
                {u.accessName}
                {u.organizationName ? ` / ${u.organizationName}` : ''}
              </td>
              <td>{u.applicationName}</td>
              <td>{u.assignedOn && formatDatetime(u.assignedOn, true)}</td>
              <td>
                <IconDelete onClick={() => setRemoveAccess(u)} />
              </td>
            </tr>
          ))}
        </tbody>
      </PagedTable>
    );
  }

  return (
    <Page header={t('title')} title={t('title')}>
      <Container fluid>
        <FormContainer key={formKey} form={form}>
          <Row>
            <Col sm={12} lg={4}>
              <Input
                form={form}
                name='firstName'
                label={t('search.firstName')}
                placeholder={t('search.firstName')}
                onEnterPressed={(e) => {
                  const next = { ...formValues, firstName: e };
                  setFormValues(next);
                  setSearch({ ...next, approvedPage: '1', assignedPage: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={4}>
              <Input
                form={form}
                name='surname'
                label={t('search.surname')}
                placeholder={t('search.surname')}
                onEnterPressed={(e) => {
                  const next = { ...formValues, surname: e };
                  setFormValues(next);
                  setSearch({ ...next, approvedPage: '1', assignedPage: '1' });
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12} lg={4}>
              <MultiSelect
                form={form}
                name='applicationIds'
                label={t('search.application')}
                okLabel={t('search.showResults')}
                options={(applications as SelectOption[]) ?? []}
                onChange={(e) => {
                  const next = { ...formValues, applicationIds: e, roleIds: [], permissionIds: [] };
                  setFormValues(next);
                  setSearch({ ...next, approvedPage: '1', assignedPage: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={4}>
              <MultiSelect
                form={form}
                name='roleIds'
                label={t('search.role')}
                okLabel={t('search.showResults')}
                options={rolesData ?? []}
                onChange={(e) => {
                  const next = { ...formValues, roleIds: e };
                  setFormValues(next);
                  setSearch({ ...next, approvedPage: '1', assignedPage: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={4}>
              <MultiSelect
                form={form}
                name='permissionIds'
                label={t('search.permission')}
                okLabel={t('search.showResults')}
                options={permissionsData ?? []}
                onChange={(e) => {
                  const next = { ...formValues, permissionIds: e };
                  setFormValues(next);
                  setSearch({ ...next, approvedPage: '1', assignedPage: '1' });
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <br />
              {search.firstName && (
                <FilterLabel
                  key='firstName'
                  label={search.firstName}
                  onRemove={() => applyFilterRemoval({ firstName: '' })}
                />
              )}
              {search.surname && (
                <FilterLabel
                  key='surname'
                  label={search.surname}
                  onRemove={() => applyFilterRemoval({ surname: '' })}
                />
              )}
              {search.applicationIds.map((a) => (
                <FilterLabel
                  key={`app-${a}`}
                  label={(applications as SelectOption[])?.find((opt) => opt.id === a)?.text ?? a}
                  onRemove={() =>
                    applyFilterRemoval({
                      applicationIds: search.applicationIds.filter((id) => id !== a),
                      roleIds: [],
                      permissionIds: []
                    })
                  }
                />
              ))}
              {search.roleIds.map((r) => (
                <FilterLabel
                  key={`role-${r}`}
                  label={rolesData?.find((opt: SelectOption) => opt.id === r)?.text ?? r}
                  onRemove={() => applyFilterRemoval({ roleIds: search.roleIds.filter((id) => id !== r) })}
                />
              ))}
              {search.permissionIds.map((p) => (
                <FilterLabel
                  key={`perm-${p}`}
                  label={permissionsData?.find((opt: SelectOption) => opt.id === p)?.text ?? p}
                  onRemove={() =>
                    applyFilterRemoval({ permissionIds: search.permissionIds.filter((id) => id !== p) })
                  }
                />
              ))}
              {hasFilters(search) && (
                <FilterReset key='reset' label={t('search.resetFilters')} onRemove={resetAll} />
              )}
            </Col>
          </Row>
          <Row className='pt-4'>
            <Col sm={12}>
              <Button type='submit' text={t('search.btnSearch')} />
            </Col>
          </Row>
        </FormContainer>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {canCreateEndUser ? renderAssignedTable() : renderApprovedTable()}
            <RemoveAccessModal
              accessRequest={removeAccess}
              onDeleteCancelled={() => setRemoveAccess(undefined)}
              onDeleteSuccess={() => {
                setRemoveAccess(undefined);
                queryClient.invalidateQueries(['approvedAccess']);
                queryClient.invalidateQueries(['assignedUsers']);
              }}
            />
          </>
        )}
      </Container>
    </Page>
  );
}

function RemoveAccessModal({
  accessRequest,
  onDeleteCancelled,
  onDeleteSuccess
}: {
  accessRequest: ApprovedAccessResponse | undefined;
  onDeleteCancelled: () => void;
  onDeleteSuccess: () => void;
}) {
  const [error, setError] = useState<GenericValidationError | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const { t } = useTranslation('pages', {
    keyPrefix: 'accessRequest.approvedAccessRequests.removeAccessModal'
  });

  const removeUserRight = async (toDelete: ApprovedAccessResponse): Promise<void> => {
    if (toDelete.accessType === UserAccessType.Role && toDelete.organizationId) {
      await usersClient.userIdRolesRoleIdOrganizationOrganizationIdDelete({
        roleId: toDelete.accessId as number,
        userId: toDelete.userId as string,
        organizationId: toDelete?.organizationId
      });
    } else if (toDelete.accessType === UserAccessType.Permission && toDelete.organizationId) {
      await usersClient.userIdPermissionsPermissionIdOrganizationOrganizationIdDelete({
        permissionId: toDelete.accessId as number,
        userId: toDelete.userId as string,
        organizationId: toDelete?.organizationId
      });
    } else if (toDelete.accessType === UserAccessType.Permission) {
      await usersClient.userIdPermissionsPermissionIdDelete({
        permissionId: toDelete.accessId as number,
        userId: toDelete.userId as string
      });
    } else if (toDelete.accessType === UserAccessType.Role) {
      await usersClient.userIdRolesRoleIdDelete({
        roleId: toDelete.accessId as number,
        userId: toDelete?.userId as string
      });
    }
  };

  async function onDeleteConfirmed() {
    if (accessRequest) {
      setDeleteLoading(true);
      let isError = false;
      try {
        setError(undefined);
        await removeUserRight(accessRequest);
      } catch (e) {
        isError = true;
        if (e instanceof ResponseError && e.response.status === 400) {
          const apiError = (await e.response.json()) as GenericValidationError;
          if (apiError) {
            setError(apiError);
          }
        }
      } finally {
        setDeleteLoading(false);
        if (!isError) onDeleteSuccess();
      }
    }
  }

  return (
    <ConfirmationModal
      header={t('title')}
      show={!!accessRequest}
      onCancel={onDeleteCancelled}
      onAccept={onDeleteConfirmed}
      disabled={error !== undefined}
      hideButtons={deleteLoading}
      noText={t('btnNo')}
      yesText={t('btnYes')}
    >
      {!error && (
        <>
          <p> {t('text')} </p>
          <div>
            <div>
              {t('lblUser')}: <strong>{accessRequest?.userDisplayName}</strong>
            </div>
            <div>
              {t('lblAccessRight')}: <strong>{accessRequest?.accessName}</strong>
            </div>
            <div>
              {t('lblApplication')}: <strong>{accessRequest?.applicationName}</strong>
            </div>
            {accessRequest?.organizationId && (
              <div>
                {t('lblOrganization')}: <strong>{accessRequest?.organizationName}</strong>
              </div>
            )}
          </div>
        </>
      )}
      {deleteLoading && <LoadingSpinner />}
      {error?.messages && error.messages.length > 0 && (
        <Message key='app_delete_err' text={error.messages[0]} type='error' />
      )}
    </ConfirmationModal>
  );
}

export default ApprovedAccessRequests;
